<template>
    <div class="opColorPicker" ref="root">
        <button type="button" class="opColorSwatch" ref="swatch" :disabled="disabled" @click="toggle" :title="css">
            <span :style="{ background: css }"></span>
        </button>

        <!-- strato trasparente sotto il popup: il clic fuori finisce qui e non arriva agli elementi sotto (canvas compresa) -->
        <div class="opColorBackdrop" v-if="open" @mousedown.prevent @click="close" @wheel="close" @contextmenu.prevent="close"></div>

        <div class="opColorPopover shadow" ref="popover" v-if="open" :style="popoverStyle">
            <div class="d-flex align-items-center gap-2">
                <input type="color" class="form-control form-control-color form-control-sm flex-shrink-0" :value="hex6"
                    @input="onHueInput" @change="commit" title="Colore" />
                <input type="text" class="form-control form-control-sm font-monospace" v-model="text"
                    @change="onTextChange" @keydown.enter.prevent="onTextChange" placeholder="#rrggbb o rgba()" />
                <button v-if="hasEyeDropper" type="button" class="btn btn-outline-secondary btn-sm flex-shrink-0" @click="pickFromScreen"
                    title="Prendi un colore dallo schermo">⌖</button>
            </div>

            <div class="d-flex align-items-center gap-2 mt-2">
                <span class="small text-muted">Opacità</span>
                <input type="range" class="form-range flex-grow-1" min="0" max="100" step="1" :value="alphaPct"
                    @input="onAlphaInput" @change="commit" :style="alphaTrackStyle" />
                <span class="small font-monospace" style="width: 38px; text-align: right;">{{alphaPct}}%</span>
            </div>

            <template v-if="paletteColors.length">
                <div class="small text-muted mt-2 mb-1">Colori del template</div>
                <div class="d-flex flex-wrap gap-1">
                    <button v-for="c in paletteColors" :key="c" type="button" class="opColorSwatch small" :class="{ current: c == css }"
                        @click="choose(c)" :title="c">
                        <span :style="{ background: c }"></span>
                    </button>
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { Rgba, normalizeColor, parseColor, toCss, toHex6 } from './colorUtils';

/**
 * Selettore colore con opacità: tinta dal selettore nativo, cursore per l'alfa,
 * campo testo, colori già usati (palette) e contagocce dove il browser lo supporta.
 * v-model: emette update:modelValue solo a scelta conclusa (rilascio del cursore, chiusura del selettore nativo).
 */
@Options({
    emits: ['update:modelValue']
})
export default class ColorPicker extends Vue {

    @Prop() modelValue: string;
    @Prop({ default: () => [] }) palette: string[];
    @Prop({ default: false }) disabled: boolean;

    open = false;
    // fixed: il popup non viene tagliato dai contenitori con overflow (es. pannello laterale che scorre)
    popoverStyle: Record<string, string> = { visibility: 'hidden' };
    color: Rgba = { r: 0, g: 0, b: 0, a: 1 };
    text = '';

    get hasEyeDropper(){
        return 'EyeDropper' in window;
    }

    get css(){
        return toCss(this.color);
    }

    get hex6(){
        return toHex6(this.color);
    }

    get alphaPct(){
        return Math.round(this.color.a * 100);
    }

    get alphaTrackStyle(){
        const c = this.color;
        return { background: `linear-gradient(to right, rgba(${c.r},${c.g},${c.b},0), rgb(${c.r},${c.g},${c.b}))`, borderRadius: '4px' };
    }

    get paletteColors(){
        const seen = new Set<string>();
        return this.palette
            .map(x => normalizeColor(x))
            .filter(x => x && !seen.has(x) && seen.add(x));
    }

    created(){
        this.fromModel(this.modelValue);
    }

    beforeUnmount(){
        this.close();
    }

    @Watch('modelValue')
    onModelValueChange(next: string){
        this.fromModel(next);
    }

    fromModel(value: string){
        this.color = parseColor(value) || { r: 0, g: 0, b: 0, a: 1 };
        this.text = this.css;
    }

    toggle(){
        if(this.open){
            this.close();
            return;
        }
        this.open = true;
        this.popoverStyle = { visibility: 'hidden' };
        // capture: intercetta lo scroll di qualunque contenitore, non solo della finestra
        window.addEventListener('scroll', this.position, true);
        window.addEventListener('resize', this.position);
        this.$nextTick(() => this.position());
    }

    close(){
        this.open = false;
        window.removeEventListener('scroll', this.position, true);
        window.removeEventListener('resize', this.position);
    }

    /** Apre il popup sotto/a destra del quadratino, oppure sopra/a sinistra se lì non c'è spazio nel viewport. */
    position(){
        const swatch = this.$refs.swatch as HTMLElement;
        const popover = this.$refs.popover as HTMLElement;
        if(!swatch || !popover)
            return;
        const gap = 4, margin = 8;
        const anchor = swatch.getBoundingClientRect();
        const width = popover.offsetWidth, height = popover.offsetHeight;
        const vw = window.innerWidth, vh = window.innerHeight;

        let left = anchor.left;
        if(left + width > vw - margin)
            left = anchor.right - width;

        let top = anchor.bottom + gap;
        if(top + height > vh - margin && anchor.top - gap - height >= margin)
            top = anchor.top - gap - height;

        // in ogni caso dentro lo schermo
        left = Math.min(Math.max(margin, left), vw - width - margin);
        top = Math.min(Math.max(margin, top), vh - height - margin);
        this.popoverStyle = { left: left + 'px', top: top + 'px' };
    }

    // anteprima mentre si trascina: il valore si emette al rilascio (commit), così l'undo non registra ogni passo
    onHueInput(ev: Event){
        const c = parseColor((ev.target as HTMLInputElement).value);
        if(c)
            this.color = { ...c, a: this.color.a };
        this.text = this.css;
    }

    onAlphaInput(ev: Event){
        this.color = { ...this.color, a: Number((ev.target as HTMLInputElement).value) / 100 };
        this.text = this.css;
    }

    onTextChange(){
        const c = parseColor(this.text);
        if(c){
            this.color = c;
            this.commit();
        } else {
            this.text = this.css;
        }
    }

    choose(value: string){
        const c = parseColor(value);
        if(!c)
            return;
        this.color = c;
        this.commit();
    }

    pickFromScreen(){
        new (window as any).EyeDropper().open()
        .then(result => {
            const c = parseColor(result.sRGBHex);
            if(c)
                this.choose(toCss({ ...c, a: this.color.a }));
        })
        .catch(() => {});
    }

    commit(){
        this.text = this.css;
        if(normalizeColor(this.modelValue) != this.css)
            this.$emit('update:modelValue', this.css);
    }

}
</script>

<style scoped>
.opColorPicker {
    position: relative;
    display: inline-block;
}
.opColorSwatch {
    width: 32px;
    height: 26px;
    padding: 2px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    /* scacchiera: si vede la trasparenza */
    background: repeating-conic-gradient(#dee2e6 0% 25%, #ffffff 0% 50%) 50% / 8px 8px;
    cursor: pointer;
    display: block;
}
.opColorSwatch span {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 2px;
}
.opColorSwatch:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}
.opColorSwatch.small {
    width: 22px;
    height: 22px;
}
.opColorSwatch.current {
    outline: 2px solid #075aa5;
    outline-offset: 1px;
}
.opColorBackdrop {
    position: fixed;
    inset: 0;
    z-index: 999;
}
.opColorPopover {
    position: fixed;
    z-index: 1000;
    width: 250px;
    padding: 10px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 6px;
}
</style>
